import * as rrweb from 'rrweb';
import { v4 as uuidv4 } from 'uuid';
import axios from 'axios';
import { humanId, adjectives, nouns, verbs, adverbs } from 'human-id';
import * as FingerprintJS from '@fingerprintjs/fingerprintjs';
import { SessionableOptions, SessionMetrics, SessionPayload } from '../types';
import { listenerHandler } from '@rrweb/types';

/**
 * Core Sessionable class for session recording
 */
export class Sessionable {
  private options: SessionableOptions & {
    apiKey: string;
    autoStart: boolean;
    metadata: Record<string, any>;
    maskInputs: boolean;
    maskTextSelector?: string;
    debug: boolean;
    useDeviceId: boolean;
  };
  private anonymousUserId: string | null = null;
  private userId: string | null = null;
  private organizationId: string | null = null;
  private userAttributes: Record<string, any> | null = null;
  private events: rrweb.eventWithTime[] = [];
  private pendingEvents: rrweb.eventWithTime[] = []; // Events waiting for ID generation
  private stopFn: listenerHandler | null = null;
  private eventsInterval: ReturnType<typeof setInterval> | null = null;
  private metrics: SessionMetrics;
  private idGenerationInProgress: boolean = false;
  // API endpoints
  private productionEndpoint = 'https://sessionable.pages.dev/api/sessions';
  private debugEndpoint = 'http://localhost:5173/api/sessions';
  private productionIdentifyEndpoint = 'https://sessionable.pages.dev/api/identify';
  private debugIdentifyEndpoint = 'http://localhost:5173/api/identify';
  private batchSize = 50;
  private batchInterval = 5000; // ms
  // Local storage keys
  private static readonly ANON_ID_STORAGE_KEY = 'sessionable_anonymous_id';

  /**
   * Creates a new Sessionable instance
   */
  constructor(options: SessionableOptions) {
    // Set default options
    this.options = {
      apiKey: options.apiKey,
      autoStart: options.autoStart || false,
      metadata: options.metadata || {},
      maskInputs: options.maskInputs || false,
      maskTextSelector: options.maskTextSelector,
      debug: options.debug || false,
      useDeviceId: options.useDeviceId !== undefined ? options.useDeviceId : true
    };
    
    // Initialize or retrieve the anonymous user ID
    this.initializeAnonymousId();

    // Initialize metrics
    this.metrics = {
      sessionId: '',
      eventsRecorded: 0,
      batchesSent: 0,
      batchesSuccessful: 0,
      connectionStatus: 'disconnected',
      lastError: null,
      lastBatchSize: 0,
      lastSuccessfulUpload: null,
      recordingStartTime: new Date(),
      isRecording: false,
    };
    
    // Set user identification if provided during initialization
    if (options.userId) {
      this.userId = options.userId;
      this.organizationId = options.organizationId || null;
      this.userAttributes = options.userAttributes || {};
      
      if (this.options.debug) {
        console.log(`[Sessionable] User identified during initialization: ${options.userId}`);
      }
    }

    // Automatically start recording if option is set
    if (this.options.autoStart) {
      this.start();
    }
  }

  /**
   * Start recording the session
   */
  public start(): void {
    if (this.metrics.isRecording) return;

    // Generate a new session ID
    const sessionId = uuidv4();
    
    // Store locally if localStorage is available
    try {
      localStorage.setItem('sessionable_session_id', sessionId);
    } catch (e) {
      // Ignore localStorage errors
    }

    // Reset events array
    this.events = [];

    // Update metrics
    this.updateMetrics({
      sessionId,
      recordingStartTime: new Date(),
      eventsRecorded: 0,
      batchesSent: 0,
      batchesSuccessful: 0,
      connectionStatus: 'connecting',
      isRecording: true
    });

    // Start recording with rrweb
    const stop = rrweb.record({
      emit: (event) => {
        // Update metrics
        this.updateMetrics({
          eventsRecorded: this.metrics.eventsRecorded + 1
        });

        // If ID is still generating, add to pending queue
        if (!this.anonymousUserId || this.idGenerationInProgress) {
          this.pendingEvents.push(event);
          if (this.options.debug && this.pendingEvents.length === 1) {
            console.log('[Sessionable] Queueing events until ID generation completes');
          }
          return;
        }

        // Push event to array
        this.events.push(event);
        
        // Send batch if we've reached the threshold
        if (this.events.length >= this.batchSize) {
          const eventsToSend = [...this.events];
          this.sendEvents(eventsToSend);
          this.events = [];
        }
      },
      // Configure rrweb options based on user settings
      inlineStylesheet: true,
      maskAllInputs: this.options.maskInputs,
      maskTextSelector: this.options.maskTextSelector,
      recordCanvas: true,
      collectFonts: true,
    });

    // Set up interval to periodically send events
    this.eventsInterval = setInterval(() => {
      // Only process events if we have an ID
      if (this.anonymousUserId && !this.idGenerationInProgress) {
        // Process any pending events first
        this.processPendingEvents();
        
        // Then process regular events
        if (this.events.length > 0) {
          const eventsToSend = [...this.events];
          this.sendEvents(eventsToSend);
          this.events = [];
        }
      }
    }, this.batchInterval);

    // Save stop function for later
    this.stopFn = stop || null;
  }

  /**
   * Stop recording the session
   */
  public stop(): void {
    if (!this.metrics.isRecording || !this.stopFn) return;

    // Stop recording
    this.stopFn();
    this.stopFn = null;

    // Clear interval
    if (this.eventsInterval) {
      clearInterval(this.eventsInterval);
      this.eventsInterval = null;
    }

    // Send any remaining events if we have an ID
    if (this.anonymousUserId && !this.idGenerationInProgress) {
      // Process any pending events first
      this.processPendingEvents();
      
      // Then send any remaining regular events
      if (this.events.length > 0) {
        const finalEvents = [...this.events];
        this.events = [];
        this.sendEvents(finalEvents);
      }
    } else if (this.options.debug) {
      console.log(`[Sessionable] Not sending ${this.pendingEvents.length + this.events.length} events on stop because ID generation is not complete`);
    }

    // Update metrics
    this.updateMetrics({
      isRecording: false
    });
  }

  /**
   * Get current session metrics
   */
  public getMetrics(): SessionMetrics {
    // Make sure we create a proper copy with Date objects intact
    return {
      ...this.metrics,
      // Ensure recordingStartTime remains a Date object
      recordingStartTime: new Date(this.metrics.recordingStartTime),
      lastSuccessfulUpload: this.metrics.lastSuccessfulUpload ? 
        new Date(this.metrics.lastSuccessfulUpload) : null
    };
  }
  
  /**
   * Get the identified user ID, if available
   */
  public getUserId(): string | null {
    return this.userId;
  }
  
  /**
   * Get the organization ID, if available
   */
  public getOrganizationId(): string | null {
    return this.organizationId;
  }
  
  /**
   * Get the user attributes, if available
   */
  public getUserAttributes(): Record<string, any> | null {
    return this.userAttributes ? { ...this.userAttributes } : null;
  }

  /**
   * Add custom metadata to the session
   */
  public addMetadata(metadata: Record<string, any>): void {
    this.options.metadata = {
      ...this.options.metadata,
      ...metadata
    };
  }
  
  /**
   * Identify the current session with a user ID
   * This links the anonymous session to an identified user
   * 
   * @param userId - The ID of the identified user
   * @param organizationId - Optional organization ID to associate with the user
   * @param userAttributes - Optional user metadata/attributes
   * @returns Promise that resolves when identification is complete
   */
  public async identify(
    userId: string, 
    organizationId?: string, 
    userAttributes?: Record<string, any>
  ): Promise<void> {
    if (!userId) {
      throw new Error('User ID is required for identification');
    }
    
    if (!this.metrics.sessionId) {
      throw new Error('Cannot identify a session that has not been started');
    }
    
    // Store the identification info locally
    this.userId = userId;
    this.organizationId = organizationId || null;
    this.userAttributes = userAttributes || {};
    
    if (this.options.debug) {
      console.log(`[Sessionable] Identifying session ${this.metrics.sessionId} with user ${userId}`);
    }
    
    try {
      // Prepare identification payload
      const payload = {
        session_id: this.metrics.sessionId,
        user_id: userId,
        ...(organizationId ? { organization_id: organizationId } : {}),
        ...(userAttributes ? { user_attributes: userAttributes } : {})
      };
      
      // Determine which endpoint to use
      const apiEndpoint = this.options.debug 
        ? this.debugIdentifyEndpoint 
        : this.productionIdentifyEndpoint;
      
      // Send identification data to the API
      await axios.post(apiEndpoint, payload, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.options.apiKey}`,
        }
      });
      
      if (this.options.debug) {
        console.log('[Sessionable] User identification successful');
      }
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Unknown error';
      console.error('[Sessionable] User identification failed:', errorMsg);
      this.updateMetrics({
        lastError: `Identification failed: ${errorMsg}`
      });
      throw error;
    }
  }

  /**
   * Send events to the Sessionable API
   */
  private async sendEvents(events: rrweb.eventWithTime[]): Promise<void> {
    if (events.length === 0) return;
    
    // Ensure we have an ID before sending
    if (!this.anonymousUserId || this.idGenerationInProgress) {
      // Queue these events and return
      this.pendingEvents.push(...events);
      if (this.options.debug) {
        console.log(`[Sessionable] Queued ${events.length} events until ID generation completes`);
      }
      return;
    }

    this.updateMetrics({
      lastBatchSize: events.length,
      batchesSent: this.metrics.batchesSent + 1
    });

    try {
      // Prepare metadata with basic information
      const metadata = {
        ...this.options.metadata,
        userAgent: navigator.userAgent,
        url: window.location.href,
        timestamp: new Date().toISOString(),
        eventCount: events.length,
      };

      // Make sure we have an ID, this should never happen at this point but let's be safe
      if (!this.anonymousUserId) {
        throw new Error('Anonymous ID not available when sending events');
      }
      
      // Prepare payload according to API format
      const payload: SessionPayload = {
        session_id: this.metrics.sessionId,
        anonymous_id: this.anonymousUserId,
        events: events,
        metadata: metadata
      };
      
      // Add user identification if available
      if (this.userId) {
        payload.user_id = this.userId;
        
        if (this.organizationId) {
          payload.organization_id = this.organizationId;
        }
        
        if (this.userAttributes) {
          payload.user_attributes = this.userAttributes;
        }
      }

      // Determine which endpoint to use
      const apiEndpoint = this.options.debug ? this.debugEndpoint : this.productionEndpoint;
      
      if (this.options.debug) {
        console.log('[Sessionable] Debug mode enabled, using endpoint:', apiEndpoint);
      }
      
      // Send to API
      await axios.post(apiEndpoint, payload, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.options.apiKey}`,
        }
      });

      // Update metrics on success
      this.updateMetrics({
        connectionStatus: 'connected',
        batchesSuccessful: this.metrics.batchesSuccessful + 1,
        lastSuccessfulUpload: new Date(),
        lastError: null
      });
    } catch (error) {
      // Update metrics on error
      this.updateMetrics({
        connectionStatus: 'error',
        lastError: error instanceof Error ? error.message : 'Unknown error'
      });
      console.error('[Sessionable] Failed to send session data:', error);
    }
  }

  /**
   * Update session metrics
   */
  private updateMetrics(updates: Partial<SessionMetrics>): void {
    this.metrics = {
      ...this.metrics,
      ...updates
    };
  }
  
  /**
   * Initialize the anonymous ID - either from localStorage or by generating a new one
   * 
   * This is asynchronous when useDeviceId is true and we need to generate a new ID.
   * When the ID is ready, it will process any pending events.
   */
  private initializeAnonymousId(): void {
    try {
      // First check if we already have an ID in localStorage
      const existingId = localStorage.getItem(Sessionable.ANON_ID_STORAGE_KEY);
      
      if (existingId) {
        // Use the existing ID
        this.anonymousUserId = existingId;
        
        if (this.options.debug) {
          console.log('[Sessionable] Using existing anonymous ID:', existingId);
        }
        
        // Notify with a custom event that ID is ready
        this.notifyIdReady(existingId);
        return;
      }
      
      // We need to create a new ID
      this.idGenerationInProgress = true;
      
      if (this.options.useDeviceId) {
        // Use FingerprintJS to generate a device-based ID
        if (this.options.debug) {
          console.log('[Sessionable] Generating device-based ID...');
        }
        
        this.generateDeviceId()
          .then(deviceBasedId => {
            if (deviceBasedId) {
              // Save the ID
              this.setAnonymousId(deviceBasedId);
              
              if (this.options.debug) {
                console.log('[Sessionable] Device-based ID generated:', deviceBasedId);
              }
            } else {
              // Fallback to human-readable ID if generation failed
              this.createHumanReadableId();
            }
          })
          .catch(error => {
            console.error('[Sessionable] Error generating device ID:', error);
            // Fallback to human-readable ID
            this.createHumanReadableId();
          });
      } else {
        // Generate a human-readable ID immediately
        this.createHumanReadableId();
      }
    } catch (e) {
      console.warn('[Sessionable] Error in ID initialization:', e);
      // Create a fallback ID that's not persisted
      this.createHumanReadableId(false);
    }
  }
  
  /**
   * Create a human-readable ID
   */
  private createHumanReadableId(persistToStorage: boolean = true): void {
    const randomSuffix = Math.floor(Math.random() * 10000).toString().padStart(4, '0');
    const newId = `${humanId({
      separator: '-',
      capitalize: false,
      adjectiveCount: 2,
      addAdverb: true
    })}-${randomSuffix}`;
    
    if (this.options.debug) {
      console.log('[Sessionable] Created human-readable ID:', newId);
    }
    
    // Set the ID and handle any pending events
    this.setAnonymousId(newId, persistToStorage);
  }
  
  /**
   * Set the anonymous ID and process any pending events
   */
  private setAnonymousId(id: string, persistToStorage: boolean = true): void {
    this.anonymousUserId = id;
    this.idGenerationInProgress = false;
    
    if (persistToStorage) {
      try {
        localStorage.setItem(Sessionable.ANON_ID_STORAGE_KEY, id);
      } catch (e) {
        console.warn('[Sessionable] Failed to store anonymous ID in localStorage:', e);
      }
    }
    
    // Notify that the ID is ready
    this.notifyIdReady(id);
    
    // Process any pending events
    this.processPendingEvents();
  }
  
  /**
   * Notify listeners that the ID is ready
   */
  private notifyIdReady(id: string): void {
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('sessionable:id_ready', { 
        detail: { anonymousId: id } 
      }));
    }
  }
  
  /**
   * Process any events that were waiting for the ID to be generated
   */
  private processPendingEvents(): void {
    if (this.pendingEvents.length > 0 && this.anonymousUserId) {
      const eventsToProcess = [...this.pendingEvents];
      this.pendingEvents = [];
      
      if (this.options.debug) {
        console.log(`[Sessionable] Processing ${eventsToProcess.length} pending events`);
      }
      
      // Add them to regular events queue
      this.events.push(...eventsToProcess);
      
      // If we've accumulated enough events, send a batch right away
      if (this.events.length >= this.batchSize) {
        const eventsToSend = [...this.events];
        this.sendEvents(eventsToSend);
        this.events = [];
      }
    }
  }
  
  /**
   * Generate a device-specific ID using FingerprintJS
   * Maps the fingerprint to a deterministic human-readable ID format
   * Format: "adverb-adjective-adjective-noun-verb-number"
   */
  private async generateDeviceId(): Promise<string | null> {
    try {
      // Initialize FingerprintJS
      const fpPromise = FingerprintJS.load();
      const fp = await fpPromise;
      
      // Get the visitor identifier
      const result = await fp.get();
      const fingerprint = result.visitorId;
      
      if (this.options.debug) {
        console.log('[Sessionable] Generated fingerprint:', fingerprint);
      }
      
      // Create a deterministic ID from the fingerprint
      const deviceId = this.createDeterministicId(fingerprint);
      
      if (this.options.debug) {
        console.log('[Sessionable] Generated device ID:', deviceId);
      }
      
      return deviceId;
    } catch (error) {
      console.error('[Sessionable] Error generating device ID:', error);
      return null;
    }
  }
  
  /**
   * Creates a deterministic human-readable ID from a fingerprint
   * Uses vocabulary from human-id package but with deterministic selection
   */
  private createDeterministicId(fingerprint: string): string {
    // Parse the fingerprint string to select different words deterministically
    // For format: "adverb-adjective-adjective-noun-verb-number"
    
    // Use chunks of the fingerprint to select different parts of speech
    const adverbIndex = parseInt(fingerprint.substring(0, 6), 16) % adverbs.length;
    const adj1Index = parseInt(fingerprint.substring(6, 12), 16) % adjectives.length;
    const adj2Index = parseInt(fingerprint.substring(12, 18), 16) % adjectives.length;
    const nounIndex = parseInt(fingerprint.substring(18, 24), 16) % nouns.length;
    const verbIndex = parseInt(fingerprint.substring(24, 30), 16) % verbs.length;
    
    // Create a numeric suffix from the last part of the fingerprint
    const numericPart = parseInt(fingerprint.substring(30), 16);
    const numericSuffix = (numericPart % 10000).toString().padStart(4, '0');
    
    // Construct the ID in the format: "adverb-adjective-adjective-noun-verb-number"
    // This matches the "addAdverb: true, adjectiveCount: 2" format of humanId
    const deviceId = `${adverbs[adverbIndex]}-${adjectives[adj1Index]}-${adjectives[adj2Index]}-${nouns[nounIndex]}-${verbs[verbIndex]}-${numericSuffix}`;
    
    return deviceId;
  }
}